package fr.frux.feature.treedetail

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.ArrowForward
import androidx.compose.material.icons.outlined.Directions
import androidx.compose.material.icons.outlined.Map
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.InitialsAvatar
import fr.frux.core.designsystem.icons.FruxIcons
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.designsystem.theme.color
import fr.frux.core.designsystem.theme.fruxColors
import fr.frux.core.maps.Location
import fr.frux.core.maps.MapLocationPreview
import fr.frux.feature.treedetail.components.MapPreviewPlaceholder
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_creator_added_recently
import fr.frux.feature.treedetail.resources.treedetail_directions
import fr.frux.feature.treedetail.resources.treedetail_map_preview_cta
import fr.frux.feature.treedetail.resources.treedetail_planned_harvests_count_plural
import fr.frux.feature.treedetail.resources.treedetail_planned_harvests_count_singular
import fr.frux.feature.treedetail.resources.treedetail_planned_harvests_title
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

/** Hauteur de l'aperçu carte de la fiche (spec #147 : ~150-180dp). */
private val MAP_PREVIEW_HEIGHT = 160.dp

/** Fenêtre au-delà de laquelle la pastille « ajouté récemment » n'a plus de sens (#283). */
private const val RECENTLY_ADDED_WINDOW_MILLIS = 30L * 24 * 60 * 60 * 1000

/**
 * Creator profile section.
 */
@OptIn(ExperimentalTime::class)
@Composable
internal fun CreatorSection(
    creatorUsername: String,
    createdAtMillis: Long,
    onClick: () -> Unit,
) {
    val isRecentlyAdded =
        (Clock.System.now().toEpochMilliseconds() - createdAtMillis) < RECENTLY_ADDED_WINDOW_MILLIS
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .clickable { onClick() }
                .padding(vertical = FruxSpacing.sm),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
        ) {
            InitialsAvatar(
                username = creatorUsername,
                size = 40.dp,
            )
            Column {
                Text(
                    text = creatorUsername,
                    style = MaterialTheme.typography.bodyLarge,
                )
                if (isRecentlyAdded) {
                    Text(
                        text = stringResource(Res.string.treedetail_creator_added_recently),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
        }

        Icon(
            imageVector = Icons.AutoMirrored.Outlined.ArrowForward,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

/**
 * Section « Récoltes prévues » (#226, restylée) : titre Sora + filet, une ligne par récolte
 * `PLANNED`/`ONGOING` avec tuile-date `tertiaryContainer`. Rendue seulement si [harvests] n'est
 * pas vide (cf. l'appelant, spec D3).
 */
@Composable
internal fun PlannedHarvestsSection(
    harvests: List<TreeHarvestItem>,
    onHarvestClick: (Long) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(bottom = FruxSpacing.sm),
            verticalAlignment = Alignment.Bottom,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Text(
                text = stringResource(Res.string.treedetail_planned_harvests_title),
                style = MaterialTheme.typography.titleLarge,
            )
            // Le compteur ne compte que les à-venir (#435) : une passée reste listée mais n'est
            // plus « à venir », et sans aucune à-venir le compteur disparaît.
            val upcoming = harvests.count { !it.expired }
            if (upcoming > 0) {
                Text(
                    text =
                        if (upcoming == 1) {
                            stringResource(Res.string.treedetail_planned_harvests_count_singular)
                        } else {
                            stringResource(Res.string.treedetail_planned_harvests_count_plural, upcoming)
                        },
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.fruxColors.accent,
                )
            }
        }
        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
        harvests.forEach { harvest ->
            Row(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .clickable { onHarvestClick(harvest.id) }
                        .padding(vertical = 14.dp),
                horizontalArrangement = Arrangement.spacedBy(12.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                HarvestDateTile(dayLabel = harvest.dayLabel, monthLabel = harvest.monthLabel)
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = harvest.title,
                        style = MaterialTheme.typography.titleSmall,
                    )
                    Text(
                        text = "${harvest.dateLabel} · ${harvest.statusLabel()}",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
                Icon(
                    imageVector = Icons.AutoMirrored.Outlined.ArrowForward,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
    }
}

/** Tuile-date `tertiaryContainer` : jour en Sora accent, mois abrégé dessous (spec zone 3). */
@Composable
private fun HarvestDateTile(
    dayLabel: String,
    monthLabel: String,
) {
    Column(
        modifier =
            Modifier
                .width(52.dp)
                .clip(RoundedCornerShape(12.dp))
                .background(MaterialTheme.colorScheme.tertiaryContainer)
                .padding(vertical = 6.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(
            text = dayLabel,
            style = MaterialTheme.typography.headlineLarge,
            color = MaterialTheme.fruxColors.accent,
        )
        Text(
            text = monthLabel,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onTertiaryContainer,
        )
    }
}

/**
 * Mini-carte de la fiche (spec zone 3) : aperçu 160 dp, coins 20, pilule « Voir sur la carte » en
 * bas à gauche. Même chemin que la mini-carte de choix de position (assistant d'ajout, #147) :
 * `FruxMap` avec épingle superposée, gestes coupés — le tap reste transmis via [onClick].
 *
 * [ready] ne devient vrai que lorsque l'item de mini-carte est réellement visible et le scroll
 * arrêté (cf. `mapPreviewReady` dans [TreeDetailScreen]) : tant que ce n'est pas le cas, un
 * placeholder statique (aucun appel MapLibre) occupe la même place. Une fois activée, la carte
 * reste affichée tant que l'item est composé — pas de destruction/recréation à chaque scroll.
 */
@Composable
private fun MapPreviewSection(
    latitude: Double,
    longitude: Double,
    ready: Boolean,
    onClick: () -> Unit,
    onDirections: () -> Unit,
) {
    var activated by remember { mutableStateOf(false) }
    if (ready) activated = true

    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .height(MAP_PREVIEW_HEIGHT)
                .clip(RoundedCornerShape(20.dp)),
    ) {
        if (activated) {
            MapLocationPreview(
                location = Location(latitude, longitude),
                pinIcon = rememberVectorPainter(FruxIcons.LocationOn),
                pinTint = MaterialTheme.fruxColors.accent,
                onClick = onClick,
                modifier = Modifier.fillMaxSize(),
            )
        } else {
            MapPreviewPlaceholder(onClick = onClick, modifier = Modifier.fillMaxSize())
        }
        Surface(
            onClick = onClick,
            shape = RoundedCornerShape(50),
            color = MaterialTheme.colorScheme.surface,
            contentColor = MaterialTheme.colorScheme.primary,
            shadowElevation = 1.dp,
            modifier =
                Modifier
                    .align(Alignment.BottomStart)
                    .padding(12.dp),
        ) {
            Row(
                modifier = Modifier.padding(horizontal = 14.dp, vertical = FruxSpacing.sm),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                Icon(
                    imageVector = Icons.Outlined.Map,
                    contentDescription = null,
                    modifier = Modifier.size(18.dp),
                )
                Text(stringResource(Res.string.treedetail_map_preview_cta), style = MaterialTheme.typography.labelLarge)
            }
        }
        // Pilule symétrique (#335) : même forme que « Voir sur la carte », à l'autre coin de
        // l'aperçu, pour ouvrir directement un itinéraire sans passer par la carte permanente.
        Surface(
            onClick = onDirections,
            shape = RoundedCornerShape(50),
            color = MaterialTheme.colorScheme.surface,
            contentColor = MaterialTheme.colorScheme.primary,
            shadowElevation = 1.dp,
            modifier =
                Modifier
                    .align(Alignment.BottomEnd)
                    .padding(12.dp),
        ) {
            Row(
                modifier = Modifier.padding(horizontal = 14.dp, vertical = FruxSpacing.sm),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                Icon(
                    imageVector = Icons.Outlined.Directions,
                    contentDescription = null,
                    modifier = Modifier.size(18.dp),
                )
                Text(stringResource(Res.string.treedetail_directions), style = MaterialTheme.typography.labelLarge)
            }
        }
    }
}

/**
 * Zone « Vie de l'arbre », mini-carte seule : item dédié de la `LazyColumn` (clé
 * [MAP_PREVIEW_KEY]) pour que [mapPreviewReady] puisse détecter son affichage réel plutôt que son
 * préchargement.
 */
@Composable
internal fun TreeLifeSectionMap(
    tree: TreeDetail,
    mapPreviewReady: Boolean,
    onMapPreviewClick: () -> Unit,
    onDirections: () -> Unit,
) {
    Column(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(start = 20.dp, end = 20.dp),
    ) {
        MapPreviewSection(
            latitude = tree.latitude,
            longitude = tree.longitude,
            ready = mapPreviewReady,
            onClick = onMapPreviewClick,
            onDirections = onDirections,
        )

        Spacer(Modifier.height(FruxSpacing.xl))
    }
}
